import type React from "react"
import { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next';
import {
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
} from "@mui/material"

type FilePreviewerProps = {
  fileUrl: string
  mimeType?: string
  fileName?: string
}

const parseCSVText = (text: string): string[][] => {
  const lines = text.split("\n").filter((line) => line.trim())
  const result: string[][] = []

  for (const line of lines) {
    const row: string[] = []
    let current = ""
    let inQuotes = false

    for (let i = 0; i < line.length; i++) {
      const char = line[i]
      if (char === '"') {
        inQuotes = !inQuotes
      } else if (char === "," && !inQuotes) {
        row.push(current.trim())
        current = ""
      } else {
        current += char
      }
    }
    row.push(current.trim())
    result.push(row)
  }
  return result
}

const RenderCsvContent: React.FC<FilePreviewerProps> = ({ fileUrl, mimeType, fileName }) => {
  const { t } = useTranslation();
  const [csvData, setCsvData] = useState<string[][]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  const fileExtension = fileUrl?.split(".").pop()?.toLowerCase() || ""
  const isCSV = mimeType === "text/csv" || fileExtension === "csv"

  useEffect(() => {
    if (isCSV) {
      setLoading(true)
      setError("")

      fetch(fileUrl)
        .then((res) => {
          if (!res.ok) throw new Error(t('filePreviewer.fetchFileFailed', 'Failed to fetch file'))
          return res.text()
        })
        .then((text) => {
          const parsedData = parseCSVText(text)
          setCsvData(parsedData)
        })
        .catch((err) => {
          console.error(err)
          setError(t('filePreviewer.csvPreviewError', 'Unable to preview this CSV file.'))
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [fileUrl, isCSV])

  if (isCSV) {
    if (loading) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: 4,
            minHeight: 200,
          }}
        >
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2 }}>
            {t('filePreviewer.loadingCsv', 'Loading CSV...')}
          </Typography>
        </Box>
      )
    }

    if (error) {
      return (
        <Box
          sx={{
            padding: 3,
            color: "error.main",
            backgroundColor: "error.light",
            borderRadius: 1,
            margin: 2,
          }}
        >
          <Typography variant="body1">{error}</Typography>
        </Box>
      )
    }

    if (csvData.length === 0) {
      return (
        <Box sx={{ padding: 3, textAlign: "center" }}>
          <Typography variant="body1" color="text.secondary">
            {t('filePreviewer.noDataInCsv', 'No data found in CSV file')}
          </Typography>
        </Box>
      )
    }

    const headers = csvData[0] || []
    const rows = csvData.slice(1)

    return (
      <Box sx={{ padding: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t('filePreviewer.csvRowsColumns', '{{rows}} rows × {{columns}} columns', { rows: rows.length, columns: headers.length })}
          </Typography>
        </Box>

        <TableContainer
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {headers.map((header, index) => (
                  <TableCell
                    key={index}
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "Primary.main",
                      color: "white",
                      minWidth: 120,
                      "&.MuiTableCell-stickyHeader": {
                        backgroundColor: "Primary.main",
                      },
                    }}
                  >
                    {header || t('filePreviewer.columnFallback', 'Column {{number}}', { number: index + 1 })}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, rowIndex) => (
                <TableRow
                  key={rowIndex}
                  hover
                  sx={{
                    "&:nth-of-type(odd)": {
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  {headers.map((_, cellIndex) => (
                    <TableCell
                      key={cellIndex}
                      sx={{
                        maxWidth: 200,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        padding: "8px 16px",
                      }}
                      title={row[cellIndex] || ""}
                    >
                      {row[cellIndex] || ""}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    )
  }

  return (
    <Box sx={{ padding: 3, textAlign: "center" }}>
      <Typography variant="body1" color="text.secondary">
        <strong>{t('filePreviewer.unsupportedFileType', 'Unsupported file type or not handled here.')}</strong>
      </Typography>
    </Box>
  )
}

export default RenderCsvContent
